import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getSpeechRules, createSpeechRule, deleteSpeechRule, getSpeechViolations, reportSpeechViolation, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaExclamationTriangle, FaGavel } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function SpeechRules({ user }) {
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showViolationModal, setShowViolationModal] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [formErrorRule, setFormErrorRule] = useState(null);
  const [savingViolation, setSavingViolation] = useState(false);
  const [formErrorViolation, setFormErrorViolation] = useState(null);
  const [newRule, setNewRule] = useState({
    rule_text: '',
    category: '',
    severity: 'low'
  });
  const [newViolation, setNewViolation] = useState({
    user_id: '',
    rule_id: '',
    context: '',
    message_content: '',
    reported_by: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesData, violationsData] = await Promise.all([
        getSpeechRules(),
        getSpeechViolations()
      ]);
      setRules(rulesData.rules || rulesData);
      setViolations(violationsData.violations || violationsData);
    } catch (error) {
      console.error('Error fetching speech data:', error);
      toast.error(extractErrorMessage(error, 'Sprachregeln konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    if (savingRule) return;
    setSavingRule(true);
    setFormErrorRule(null);
    try {
      await createSpeechRule(newRule);
      setNewRule({ rule_text: '', category: '', severity: 'low' });
      setShowRuleModal(false);
      toast.success('Sprachregel erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating speech rule:', error);
      setFormErrorRule(extractErrorMessage(error, 'Fehler beim Erstellen der Sprachregel'));
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await deleteSpeechRule(id);
      toast.success('Sprachregel gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting speech rule:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen der Sprachregel'));
    }
  };

  const handleReportViolation = async (e) => {
    e.preventDefault();
    if (savingViolation) return;
    setSavingViolation(true);
    setFormErrorViolation(null);
    try {
      await reportSpeechViolation(newViolation);
      setNewViolation({ user_id: '', rule_id: '', context: '', message_content: '', reported_by: '' });
      setShowViolationModal(false);
      toast.success('Verstoß gemeldet');
      fetchData();
    } catch (error) {
      console.error('Error reporting violation:', error);
      setFormErrorViolation(extractErrorMessage(error, 'Fehler beim Melden des Verstoßes'));
    } finally {
      setSavingViolation(false);
    }
  };

  const closeRuleModal = () => {
    if (savingRule) return;
    setShowRuleModal(false);
    setFormErrorRule(null);
  };

  const closeViolationModal = () => {
    if (savingViolation) return;
    setShowViolationModal(false);
    setFormErrorViolation(null);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#f04747';
      case 'medium': return '#faa61a';
      case 'low': return '#43b581';
      default: return '#99aab5';
    }
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        {/* Speech Rules Section */}
        <div className="page-header">
          <h1><FaGavel /> Speech Rules</h1>
          <button className="add-btn" onClick={() => setShowRuleModal(true)}>
            <FaPlus /> New Rule
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Rule</th>
                    <th>Category</th>
                    <th>Severity</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length > 0 ? (
                    rules.map(rule => (
                      <tr key={rule.id}>
                        <td>{rule.id}</td>
                        <td>{rule.rule_text}</td>
                        <td>{rule.category || '-'}</td>
                        <td>
                          <span style={{ color: getSeverityColor(rule.severity), fontWeight: 'bold' }}>
                            {rule.severity}
                          </span>
                        </td>
                        <td>
                          <button className="delete-btn" onClick={() => handleDeleteRule(rule.id)}>
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-data">No speech rules defined</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Violations Section */}
            <div className="page-header" style={{ marginTop: '2rem' }}>
              <h2><FaExclamationTriangle /> Speech Violations</h2>
              <button className="add-btn" onClick={() => setShowViolationModal(true)}>
                <FaPlus /> Report Violation
              </button>
            </div>

            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Rule ID</th>
                    <th>Context</th>
                    <th>Message Content</th>
                    <th>Reported By</th>
                  </tr>
                </thead>
                <tbody>
                  {violations.length > 0 ? (
                    violations.map(v => (
                      <tr key={v.id}>
                        <td>
                          {new Date(v.created_at).toLocaleDateString('de-DE', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </td>
                        <td>{v.user_id}</td>
                        <td>{v.rule_id}</td>
                        <td>{v.context || '-'}</td>
                        <td>{v.message_content || '-'}</td>
                        <td>{v.reported_by || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data">No violations recorded</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* New Rule Modal */}
        {showRuleModal && (
          <div className="modal-overlay" onClick={closeRuleModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>New Speech Rule</h2>
              {formErrorRule && <div className="shared-form-error-banner">{formErrorRule}</div>}
              <form onSubmit={handleCreateRule}>
                <div className="form-group">
                  <label>Rule Text</label>
                  <textarea
                    value={newRule.rule_text}
                    onChange={e => setNewRule({...newRule, rule_text: e.target.value})}
                    placeholder="Describe the speech rule..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={newRule.category}
                    onChange={e => setNewRule({...newRule, category: e.target.value})}
                    placeholder="e.g., language, tone, address"
                  />
                </div>
                <div className="form-group">
                  <label>Severity</label>
                  <select
                    value={newRule.severity}
                    onChange={e => setNewRule({...newRule, severity: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeRuleModal} disabled={savingRule}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={savingRule}>{savingRule ? 'Speichert…' : 'Create Rule'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Report Violation Modal */}
        {showViolationModal && (
          <div className="modal-overlay" onClick={closeViolationModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Report Speech Violation</h2>
              {formErrorViolation && <div className="shared-form-error-banner">{formErrorViolation}</div>}
              <form onSubmit={handleReportViolation}>
                <div className="form-group">
                  <label>User</label>
                  <UserSelect
                    value={newViolation.user_id}
                    onChange={id => setNewViolation({...newViolation, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Rule</label>
                  <select
                    value={newViolation.rule_id}
                    onChange={e => setNewViolation({...newViolation, rule_id: e.target.value})}
                    required
                  >
                    <option value="">Select a rule...</option>
                    {rules.map(rule => (
                      <option key={rule.id} value={rule.id}>{rule.rule_text}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Context</label>
                  <textarea
                    value={newViolation.context}
                    onChange={e => setNewViolation({...newViolation, context: e.target.value})}
                    placeholder="What was the context?"
                  />
                </div>
                <div className="form-group">
                  <label>Message Content</label>
                  <textarea
                    value={newViolation.message_content}
                    onChange={e => setNewViolation({...newViolation, message_content: e.target.value})}
                    placeholder="What was said?"
                  />
                </div>
                <div className="form-group">
                  <label>Reported By</label>
                  <input
                    type="text"
                    value={newViolation.reported_by}
                    onChange={e => setNewViolation({...newViolation, reported_by: e.target.value})}
                    placeholder="Who reported this?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeViolationModal} disabled={savingViolation}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={savingViolation}>{savingViolation ? 'Speichert…' : 'Report Violation'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default SpeechRules;
